import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';

// Lease Registration — READ-ONLY view of the fee assessments that live on slf-legal.co.
// Every figure (legal fee, fees/taxes, step, In-Progress/Completed) is computed on the public
// site from ONE shared module and handed over ready-made, so these numbers can never drift from
// what the public back office and the client's own share page show. Nothing here writes: no
// create, no edit, no delete — those stay on slf-legal.co (see server/src/services/leaseReg.ts).

type Row = {
  id: number; refId: string; shareCode: string;
  clientName: string | null; developmentNo: string | null; jobId: string | null;
  agentId: number; agent: string; typeLabel: string;
  propertyValue: number; legalFeeTotal: number | null; feesTaxes: number;
  paidCorporate: boolean; paidDedicated: boolean;
  stepNo: number; stepTitle: string; stepTotal: number; isComplete: boolean;
  updatedAt: string; clientUrl: string; developerUrl: string; manageUrl: string;
};

// 10.5 → 11.5px (Calem, report 66c9d174, 2nd opening: "Do the same for all other menus in the SLF system"). Then Active
// Jobs' headings, as every staff list's (report 4bd4ae4e, 2nd opening: "Make all menus in the SLF system look like this:
// the same height, text color, no bold, horizontal line colors etc."): black, --text-primary, at the regular weight, where
// they were dark grey in bold, 10px above and below on one 16px line, a 37px band, white where it was light grey.
const th: React.CSSProperties = { fontSize: 11.5, fontWeight: 400, color: 'var(--text-primary)', padding: '10px 10px', lineHeight: '16px', borderBottom: '1px solid var(--border-strong)', textAlign: 'left', whiteSpace: 'nowrap', background: 'var(--bg-surface)' };
const td: React.CSSProperties = { fontSize: 12.5, padding: '9px 10px', borderBottom: '1px solid var(--border)', verticalAlign: 'top' };
const rt: React.CSSProperties = { textAlign: 'right', whiteSpace: 'nowrap' };
const ddl: React.CSSProperties = { padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', cursor: 'pointer' };
const NAVY = '#0e2a4a';

const money = (n: number) => Number(n || 0).toLocaleString('en-US');
// "29 Jul 2026" — same rendering as the public back office.
const fmtDate = (iso: string) => { const d = new Date(iso); return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); };

const PaidTick = ({ title }: { title: string }) => (
  <i className="ti ti-circle-check-filled" title={title} style={{ color: '#15803D', fontSize: 14, marginLeft: 5, verticalAlign: '-2px' }} aria-hidden />
);

function ActionsMenu({ row, onCopied }: { row: Row; onCopied: (msg: string) => void }) {
  const { t } = useTranslation();
  // position:fixed, not absolute — the table lives in a scrolling container, and an absolutely
  // positioned menu gets clipped by it (the last rows would show only a sliver of the menu).
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);
  const open = !!at;
  const boxRef = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const setOpen = (on: boolean) => {
    if (!on) { setAt(null); return; }
    const r = btnRef.current?.getBoundingClientRect();
    setAt(r ? { x: r.right, y: r.bottom } : { x: 0, y: 0 });
  };
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node) && !btnRef.current?.contains(e.target as Node)) setAt(null); };
    const close = () => setAt(null);
    document.addEventListener('mousedown', away);
    window.addEventListener('scroll', close, true);   // any ancestor scroll → the anchor moved
    window.addEventListener('resize', close);
    return () => { document.removeEventListener('mousedown', away); window.removeEventListener('scroll', close, true); window.removeEventListener('resize', close); };
  }, [open]);
  const copy = async (url: string, label: string) => {
    try { await navigator.clipboard.writeText(url); onCopied(label); }
    catch { window.prompt(t('lr_copy_manual'), url); }
    setOpen(false);
  };
  const go = (url: string) => { window.open(url, '_blank', 'noopener'); setOpen(false); };
  const item = (label: string, fn: () => void, icon: string) => (
    <button onClick={fn} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '8px 12px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12.5, color: 'var(--text-primary)' }}
      onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-active)')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
      <i className={`ti ti-${icon}`} style={{ fontSize: 13, color: 'var(--text-tertiary)' }} aria-hidden />{label}
    </button>
  );
  const group = (label: string) => (
    <div style={{ padding: '8px 12px 3px', fontSize: 9.5, fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-tertiary)', borderTop: '1px solid var(--border)' }}>{label}</div>
  );
  return (
    <>
      <button ref={btnRef} onClick={() => setOpen(!open)} style={{ ...ddl, padding: '5px 10px', fontSize: 12.5, whiteSpace: 'nowrap' }}>
        {t('lr_actions')} <i className="ti ti-chevron-down" style={{ fontSize: 11 }} aria-hidden />
      </button>
      {at && (
        <div ref={boxRef} style={{ position: 'fixed', left: Math.max(8, Math.min(at.x - 272, window.innerWidth - 280)), top: Math.min(at.y + 4, window.innerHeight - 250), zIndex: 6000, width: 272, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10, boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
          {item(t('lr_open_full'), () => go(row.manageUrl), 'external-link')}
          {group(t('lr_client_link'))}
          {item(t('lr_copy_url'), () => copy(row.clientUrl, t('lr_copied_client')), 'copy')}
          {item(t('lr_open_link'), () => go(row.clientUrl), 'arrow-up-right')}
          {group(t('lr_dev_link'))}
          {item(t('lr_copy_url'), () => copy(row.developerUrl, t('lr_copied_dev')), 'copy')}
          {item(t('lr_open_link'), () => go(row.developerUrl), 'arrow-up-right')}
        </div>
      )}
    </>
  );
}

export default function LeaseReg({ embedded = false }: { embedded?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState<'in_progress' | 'completed'>('in_progress');
  const [agent, setAgent] = useState('all');
  const [q, setQ] = useState('');
  const [toast, setToast] = useState<{ msg: string; n: number } | null>(null);
  const toastSeq = useRef(0);
  const say = (msg: string) => { toastSeq.current += 1; setToast({ msg, n: toastSeq.current }); };

  const load = () => {
    setLoading(true); setErr('');
    api.leaseRegList()
      .then((d: any) => setRows(d.rows || []))
      .catch((e: any) => setErr(e.message || 'Failed to load'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (!toast) return; const h = setTimeout(() => setToast(null), 2200); return () => clearTimeout(h); }, [toast]);

  // Agent options come from the rows themselves (same as the public back office).
  const agents = useMemo(() => {
    const m = new Map<number, string>();
    for (const r of rows) if (r.agentId != null) m.set(r.agentId, r.agent || '—');
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  const needle = q.trim().toLowerCase();
  // Both filters apply BEFORE the tab split, so the badge counts always match the rows on screen.
  const pool = useMemo(() => rows
    .filter(r => agent === 'all' || String(r.agentId) === agent)
    .filter(r => !needle || `${r.refId} ${r.clientName || ''} ${r.developmentNo || ''} ${r.jobId || ''}`.toLowerCase().includes(needle)),
  [rows, agent, needle]);
  const visible = useMemo(() => pool.filter(r => (tab === 'completed' ? r.isComplete : !r.isComplete)), [pool, tab]);
  const completedCount = pool.filter(r => r.isComplete).length;
  const inProgressCount = pool.length - completedCount;

  const tabBtn = (k: 'in_progress' | 'completed', label: string, n: number) => (
    <button key={k} onClick={() => setTab(k)} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 4px', marginRight: 20, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13.5, fontWeight: 700, color: tab === k ? NAVY : 'var(--text-tertiary)', borderBottom: `2px solid ${tab === k ? NAVY : 'transparent'}` }}>
      {label}
      <span style={{ fontSize: 11, fontWeight: 800, minWidth: 20, padding: '1px 7px', borderRadius: 999, background: tab === k ? NAVY : 'var(--c-gray-100)', color: tab === k ? '#fff' : 'var(--text-secondary)' }}>{n}</span>
    </button>
  );

  return (
    <div style={embedded
      ? { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--bg-app)' }
      : { height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-app)' }}>
      {!embedded && (
        <header style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
          <button onClick={() => navigate('/')} title="SLF" style={{ width: 30, height: 30, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer' }}><img className="app-logo" src="/logo.png" alt="SLF" style={{ width: 28, height: 28, objectFit: 'contain' }} /></button>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{t('mod_lease_reg')}</div>
        </header>
      )}

      <div style={{ padding: '14px 18px 0', background: 'var(--bg-surface)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: '#b4894a' }}>{t('lr_kicker')}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)' }}>{t('mod_lease_reg')}</div>
          </div>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('lr_search')} style={{ ...ddl, fontWeight: 400, cursor: 'text', width: 210 }} />
          <select value={agent} onChange={e => setAgent(e.target.value)} style={ddl} title={t('lr_agent')}>
            <option value="all">{t('lr_all_agents')}</option>
            {agents.map(([id, name]) => <option key={id} value={String(id)}>{name}</option>)}
          </select>
          <button onClick={load} disabled={loading} title={t('refresh')} style={{ ...ddl, padding: '7px 11px' }}>
            <i className="ti ti-refresh" style={{ fontSize: 14, display: 'inline-block', animation: loading ? 'spin 1s linear infinite' : undefined }} aria-hidden />
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {tabBtn('in_progress', t('lr_in_progress'), inProgressCount)}
          {tabBtn('completed', t('lr_completed'), completedCount)}
          <div style={{ flex: 1 }} />
          <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: 'var(--text-tertiary)' }}>
            <i className="ti ti-lock" style={{ fontSize: 12 }} aria-hidden />{t('lr_readonly')}
          </span>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '16px 18px', minHeight: 0 }}>
        {loading ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>
          : err ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#B42318', background: '#FDE8E8', border: '1px solid #F5B5B1', borderRadius: 8, padding: '10px 13px' }}>
              <i className="ti ti-alert-triangle" aria-hidden />{err}
            </div>
          ) : (
              <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'visible', background: 'var(--bg-surface)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1180 }}>
                  <thead><tr>
                    <th style={{ ...th, width: 76 }}>{t('lr_ref')}</th>
                    <th style={th}>{t('lr_client')}</th>
                    <th style={{ ...th, width: 110 }}>{t('lr_agent')}</th>
                    <th style={th}>{t('lr_development')}</th>
                    <th style={{ ...th, width: 90 }}>{t('lr_job')}</th>
                    <th style={{ ...th, width: 108 }}>{t('lr_type')}</th>
                    <th style={{ ...th, minWidth: 168 }}>{t('lr_status')}</th>
                    <th style={{ ...th, ...rt, width: 118 }}>{t('lr_lease_value')}</th>
                    <th style={{ ...th, ...rt, width: 118 }}>{t('lr_legal_fee')}</th>
                    <th style={{ ...th, ...rt, width: 118 }}>{t('lr_fees_taxes')}</th>
                    <th style={{ ...th, width: 96 }}>{t('lr_saved')}</th>
                    <th style={{ ...th, width: 92, borderRight: 'none' }}>{t('lr_actions')}</th>
                  </tr></thead>
                  <tbody>
                    {visible.length === 0 && (
                      <tr><td colSpan={12} style={{ ...td, textAlign: 'center', padding: 28, color: 'var(--text-tertiary)', borderBottom: 'none' }}>
                        {needle || agent !== 'all' ? t('lr_no_match') : t('lr_none')}
                      </td></tr>
                    )}
                    {visible.map(r => (
                      <tr key={r.id}>
                        <td style={{ ...td, fontWeight: 800, color: NAVY, whiteSpace: 'nowrap' }}>{r.refId}</td>
                        <td style={{ ...td, fontWeight: 600 }}>{r.clientName || '—'}</td>
                        <td style={td}><span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#EEF2F7', color: NAVY, whiteSpace: 'nowrap' }}>{r.agent}</span></td>
                        <td style={td}>{r.developmentNo || '—'}</td>
                        <td style={td}>{r.jobId
                          ? (/^https?:\/\//i.test(r.jobId)
                            ? <a href={r.jobId} target="_blank" rel="noopener noreferrer" title={r.jobId} style={{ color: 'var(--accent)', wordBreak: 'break-all' }}>{r.jobId}</a>
                            : r.jobId)
                          : '—'}</td>
                        <td style={td}>{r.typeLabel}</td>
                        <td style={td}>
                          {r.isComplete
                            ? <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 999, background: '#ECFDF5', color: '#15803D' }}>{t('lr_completed')}</span>
                            : (<>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700 }}>
                                <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#b4894a', flexShrink: 0 }} />
                                {t('lr_step_of', { n: r.stepNo, total: r.stepTotal })}
                              </div>
                              <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{r.stepTitle}</div>
                            </>)}
                        </td>
                        <td style={{ ...td, ...rt }}>{money(r.propertyValue)} THB</td>
                        <td style={{ ...td, ...rt }}>
                          {r.legalFeeTotal !== null ? <>{money(r.legalFeeTotal)} THB{r.paidCorporate && <PaidTick title={t('lr_paid')} />}</>
                            : <span style={{ color: '#B45309', fontStyle: 'italic' }}>{t('lr_review')}</span>}
                        </td>
                        <td style={{ ...td, ...rt }}>{money(r.feesTaxes)} THB{r.paidDedicated && <PaidTick title={t('lr_paid')} />}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>{fmtDate(r.updatedAt)}</td>
                        <td style={{ ...td, borderRight: 'none' }}><ActionsMenu row={r} onCopied={say} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </div>

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 6000, background: NAVY, color: '#fff', fontSize: 12.5, fontWeight: 600, padding: '9px 16px', borderRadius: 999, boxShadow: 'var(--shadow-md)' }}>{toast.msg}</div>
      )}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
